import { useEffect, useRef, useState } from "react";
import { FilesetResolver, FaceDetector } from "@mediapipe/tasks-vision";
import { Loader2, Camera, AlertTriangle } from "lucide-react";

/**
 * On-cam liveness gate.
 * Samples ~5fps for 3s using MediaPipe FaceDetector.
 * Computes: face_hit_rate, motion_score (luma stddev inside bbox), bbox_movement.
 * Calls onPass({frame_count, face_hit_rate, motion_score, bbox_movement, measured_at})
 * Calls onFail(reason) on threshold failure.
 *
 * When `debug` is true, render a side panel with live metric values + thresholds.
 */
export default function CameraLiveness({ thresholds, onPass, onFail, debug = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState("init"); // init | running | done | error
  const [progress, setProgress] = useState(0);
  const [errMsg, setErrMsg] = useState("");
  const [liveMetrics, setLiveMetrics] = useState({ frames: 0, hits: 0, faceHit: 0, motion: 0, bbox: 0 });

  useEffect(() => {
    let cancelled = false;
    let stream;
    let detector;

    async function run() {
      try {
        // 1) Camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 360 }, facingMode: "user" },
          audio: false,
        });
        if (cancelled) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});

        // 2) MediaPipe FaceDetector
        const filesetResolver = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm"
        );
        detector = await FaceDetector.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.5,
        });
        if (cancelled) return;

        setPhase("running");

        // 3) Sample loop
        const samples = [];
        const start = performance.now();
        const duration = thresholds?.sample_duration_ms ?? 3000;
        let lastBboxCenter = null;
        let bboxMovement = 0;
        let prevFrameLuma = null;
        let motionSum = 0;
        let motionCount = 0;

        async function tick() {
          if (cancelled) return;
          const elapsed = performance.now() - start;
          setProgress(Math.min(1, elapsed / duration));
          if (elapsed >= duration) {
            finalize();
            return;
          }
          const v = videoRef.current;
          if (v && v.readyState >= 2) {
            const res = detector.detectForVideo(v, performance.now());
            const det = res?.detections?.[0];
            const hit = !!det;
            samples.push(hit);
            if (det) {
              const b = det.boundingBox;
              const cx = b.originX + b.width / 2;
              const cy = b.originY + b.height / 2;
              if (lastBboxCenter) {
                bboxMovement = Math.max(
                  bboxMovement,
                  Math.hypot(cx - lastBboxCenter.x, cy - lastBboxCenter.y)
                );
              }
              lastBboxCenter = { x: cx, y: cy };

              // Luma stddev within bbox (downsample via 64×64 canvas)
              const c = canvasRef.current;
              const ctx = c.getContext("2d", { willReadFrequently: true });
              c.width = 64;
              c.height = 64;
              const sx = Math.max(0, b.originX);
              const sy = Math.max(0, b.originY);
              const sw = Math.min(v.videoWidth - sx, Math.max(8, b.width));
              const sh = Math.min(v.videoHeight - sy, Math.max(8, b.height));
              ctx.drawImage(v, sx, sy, sw, sh, 0, 0, 64, 64);
              const imgData = ctx.getImageData(0, 0, 64, 64).data;
              let sum = 0;
              const lumas = new Float32Array(64 * 64);
              for (let i = 0, j = 0; i < imgData.length; i += 4, j++) {
                const l = 0.2126 * imgData[i] + 0.7152 * imgData[i + 1] + 0.0722 * imgData[i + 2];
                lumas[j] = l;
                sum += l;
              }
              const mean = sum / lumas.length;
              let varSum = 0;
              for (let i = 0; i < lumas.length; i++) varSum += (lumas[i] - mean) ** 2;
              const std = Math.sqrt(varSum / lumas.length);
              if (prevFrameLuma !== null) {
                motionSum += Math.abs(std - prevFrameLuma);
                motionCount += 1;
              }
              prevFrameLuma = std;
            }
            // Live debug metrics
            const hits = samples.filter(Boolean).length;
            setLiveMetrics({
              frames: samples.length,
              hits,
              faceHit: samples.length > 0 ? hits / samples.length : 0,
              motion: motionCount > 0 ? motionSum / motionCount : 0,
              bbox: bboxMovement,
            });
          }
          setTimeout(tick, 200);
        }

        function finalize() {
          try {
            const hits = samples.filter(Boolean).length;
            const frame_count = samples.length;
            const face_hit_rate = frame_count > 0 ? hits / frame_count : 0;
            const motion_score = motionCount > 0 ? motionSum / motionCount : 0;
            const measured_at = Math.floor(Date.now() / 1000);
            setPhase("done");
            const passed =
              frame_count >= (thresholds?.min_frames ?? 10) &&
              face_hit_rate >= (thresholds?.min_face_hit_rate ?? 0.8) &&
              motion_score >= (thresholds?.min_motion_score ?? 2.0);
            if (passed) {
              onPass({
                frame_count,
                face_hit_rate,
                motion_score,
                bbox_movement: bboxMovement,
                measured_at,
              });
            } else {
              const reasons = [];
              if (frame_count < (thresholds?.min_frames ?? 10)) reasons.push("not enough frames");
              if (face_hit_rate < (thresholds?.min_face_hit_rate ?? 0.8)) reasons.push("face not visible");
              if (motion_score < (thresholds?.min_motion_score ?? 2.0)) reasons.push("looks static");
              onFail(reasons.join(", ") || "verification failed");
            }
          } finally {
            try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
          }
        }

        tick();
      } catch (e) {
        console.error(e);
        if (cancelled) return;
        setPhase("error");
        setErrMsg(e?.message || "Could not access camera");
        onFail(e?.message || "camera_unavailable");
      }
    }

    run();
    return () => {
      cancelled = true;
      try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      try { detector?.close(); } catch { /* ignore */ }
    };
  }, [thresholds, onPass, onFail]);

  return (
    <div className="flex flex-col items-center gap-4" data-testid="camera-liveness">
      <div className="relative w-72 aspect-video rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0F] shadow-[0_20px_60px_-20px_rgba(0,240,255,0.35)]">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute inset-0 ring-2 ring-[#00F0FF]/40 rounded-2xl pointer-events-none" />
        <div
          className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] transition-[width]"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {debug && (
        <div className="w-72 p-3 rounded-xl bg-black/60 border border-[#00F0FF]/30 font-mono text-[11px] leading-relaxed" data-testid="liveness-debug-panel">
          <div className="text-[#00F0FF] uppercase tracking-[0.2em] text-[9px] mb-1.5">Liveness · debug</div>
          <DebugRow label="phase" value={phase} />
          <DebugRow label="frames" value={`${liveMetrics.frames} / ${thresholds?.min_frames ?? 10}+`} />
          <DebugRow label="face hits" value={liveMetrics.hits} />
          <DebugRow label="face_hit_rate" value={liveMetrics.faceHit.toFixed(3)}
            threshold={`>= ${(thresholds?.min_face_hit_rate ?? 0.6).toFixed(2)}`}
            ok={liveMetrics.faceHit >= (thresholds?.min_face_hit_rate ?? 0.6)} />
          <DebugRow label="motion_score" value={liveMetrics.motion.toFixed(3)}
            threshold={`>= ${(thresholds?.min_motion_score ?? 0.5).toFixed(2)}`}
            ok={liveMetrics.motion >= (thresholds?.min_motion_score ?? 0.5)} />
          <DebugRow label="bbox_movement" value={liveMetrics.bbox.toFixed(2)} />
        </div>
      )}

      <div className="text-center">
        {phase === "init" && (
          <p className="text-sm text-white/60 flex items-center gap-2"><Camera className="w-4 h-4" />Requesting camera…</p>
        )}
        {phase === "running" && (
          <p className="text-sm text-white/80 flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" />Verifying you're a real person…</p>
        )}
        {phase === "done" && <p className="text-sm text-white/40">Verified.</p>}
        {phase === "error" && (
          <p className="text-sm text-[#FF007F] flex items-center gap-2 justify-center"><AlertTriangle className="w-4 h-4" />{errMsg}</p>
        )}
      </div>
    </div>
  );
}

function DebugRow({ label, value, threshold, ok }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-white/40">{label}</span>
      <span className="flex items-center gap-2">
        <span className={ok === undefined ? "text-white/80" : ok ? "text-emerald-400" : "text-[#FF007F]"}>{value}</span>
        {threshold && <span className="text-white/30">{threshold}</span>}
      </span>
    </div>
  );
}
